/**
 * HalftimeOverlay — the 2D layer of the halftime side-swap cinematic. It sits
 * on top of the in-arena shot of the local player's mannequin (rendered by
 * {@link HalftimeCinematic}) and shows the new faction title up top — the
 * "you're on the other side now" reveal — plus the phase countdown.
 * Non-interactive (pointer-events off; auto-dismissed when the halftime
 * phase ends and the buy phase begins).
 */
export class HalftimeOverlay {
  private root: HTMLDivElement;
  private countEl: HTMLElement | null = null;
  private lastSec = -1;

  constructor() {
    const el = document.createElement("div");
    el.id = "halftime-overlay";
    el.style.cssText = [
      "position:fixed",
      "inset:0",
      "z-index:9998",
      "display:none",
      "pointer-events:none",
      "overflow:hidden",
      "font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif",
    ].join(";");
    document.body.appendChild(el);
    this.root = el;
  }

  show(team: "ct" | "t"): void {
    this.lastSec = -1;
    const faction = team === "ct" ? "Counter-Terrorist" : "Terrorist";
    const color = team === "ct" ? "#5b8def" : "#ff7a33";
    this.root.innerHTML = `
      <style>
        #halftime-overlay .hft-grad{position:absolute;inset:0;background:linear-gradient(180deg,
          rgba(8,8,12,0.62) 0%, rgba(8,8,12,0) 26%, rgba(8,8,12,0) 60%, rgba(8,8,12,0.72) 100%);}
        #halftime-overlay .hft-top{position:absolute;top:7%;left:0;right:0;text-align:center;}
        #halftime-overlay .hft-eyebrow{font-size:13px;letter-spacing:4px;text-transform:uppercase;
          color:#cfd6e6;margin-bottom:10px;animation:hft-fade .6s ease both;}
        #halftime-overlay .hft-team{font-size:64px;font-weight:800;font-style:italic;line-height:1.05;
          letter-spacing:1px;text-transform:uppercase;color:${color};
          text-shadow:0 0 34px rgba(0,0,0,0.55),0 6px 22px rgba(0,0,0,0.5);
          animation:hft-teamin .7s cubic-bezier(.2,.9,.25,1) both;}
        #halftime-overlay .hft-count{position:absolute;bottom:7%;left:0;right:0;text-align:center;
          font-size:30px;font-weight:800;color:#fff;line-height:1;
          text-shadow:0 3px 16px rgba(0,0,0,0.6);font-variant-numeric:tabular-nums;
          animation:hft-fade .6s ease .2s both;}
        @keyframes hft-teamin{0%{opacity:0;transform:scale(1.9)}100%{opacity:1;transform:scale(1)}}
        @keyframes hft-pop{0%{transform:scale(1.45);opacity:.4}100%{transform:scale(1);opacity:1}}
        @keyframes hft-fade{0%{opacity:0}100%{opacity:1}}
      </style>
      <div class="hft-grad"></div>
      <div class="hft-top">
        <div class="hft-eyebrow">Halftime — switching sides</div>
        <div class="hft-team">${faction}</div>
      </div>
      <div class="hft-count"></div>
    `;
    this.countEl = this.root.querySelector(".hft-count");
    this.root.style.display = "block";
  }

  /** Update the countdown number (seconds left). Re-pops on each change. */
  setCountdown(sec: number): void {
    const s = Math.max(0, Math.floor(sec));
    if (!this.countEl || s === this.lastSec) return;
    this.lastSec = s;
    this.countEl.textContent = String(s);
    this.countEl.style.animation = "none";
    void this.countEl.offsetWidth; // reflow so the pop animation retriggers
    this.countEl.style.animation = "hft-pop .4s ease";
  }

  hide(): void {
    this.root.style.display = "none";
    this.root.innerHTML = "";
    this.countEl = null;
  }
}
